import React, {useCallback, useContext, useState, useEffect, useRef} from 'react';
import {Popup, setOptions, Button, Checkbox} from "@mobiscroll/react";
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import '../styles/dashboardHeaderQuestion.css';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import {
    Autosave,
    BlockQuote,
    Bold,
    Essentials,
    Italic,
    Paragraph,
    RemoveFormat,
    Subscript,
    Superscript,
    Underline,
    CodeBlock,
    BalloonEditor,
    BlockToolbar,
    GeneralHtmlSupport,
    HtmlComment
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faXmark} from "@fortawesome/pro-duotone-svg-icons";
import Empty from "../utils/Empty";
import LoadingAvatar from "../utils/LoadingAvatar";
import {NetworkContext} from "../utils/NetworkContext";

setOptions({
    theme: 'ios',
    themeVariant: 'light',
});

function DashboardHeaderQuestion({locale, dialog, Dialog, width, setToast, toast, quickQuestion, setQuickQuestion}) {
    const {wsCall, wsResponse, setWsResponse} = useContext(NetworkContext);
    const editorWrapRef = useRef(null);
    const [editorInstance, setEditorInstance] = useState(null);
    const [text, setText] = useState('<p></p>');
    const [context, setContext] = useState(true);
    const [loading, setLoading] = useState(false);
    const [answer, setAnswer] = useState(null);

    const DialogClose = useCallback(() => {
        setQuickQuestion({});
        if (!Empty(toast)) {
            setToast({});
        }
        Dialog({view: 'close'});
    }, [Dialog, toast]);

    const ButtonAsk = () => {
        setToast({message: (locale.dashboardHeaderQuestion.e + '...'), color: 'info', display: 'bottom', duration: false});
        setLoading(true);

        const message = {
            type: 'wss',
            path: 'question',
            action: 'post',
            data: {
                text: text,
                tab: quickQuestion.id,
                context: context
            }
        };
        wsCall(message);
    }

    const EditorUpdate = data => {
        let content = "<p></p>";

        if (data) {
            content = data;
        }
        setText(content);
    }

    const PostQuestion = useCallback(data => {
        if (data.text) {
            setAnswer(data.text);
        }
        if (data.toast) {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!Empty(wsResponse) && wsResponse.action === 'post' && wsResponse.path === 'question') {
            PostQuestion(wsResponse.data);
            setWsResponse({});
            setToast({});
        }
    }, [wsResponse, PostQuestion, setWsResponse]);

    useEffect(() => {
        const t = setTimeout(() => {
            const el = editorWrapRef.current;
            if (!el) return;
            const stop = (e) => e.stopPropagation();
            ['keydown', 'keyup', 'keypress'].forEach((type) => el.addEventListener(type, stop));
            el._cleanup = () =>
                ['keydown', 'keyup', 'keypress'].forEach((type) => el.removeEventListener(type, stop));
        }, 0);
        return () => {
            clearTimeout(t);
            editorWrapRef.current?._cleanup?.();
        };
    }, []);

    return (
        <Popup className="cs-dashboard-header-question-popup" display="center" width={answer ? null : 600} scrollLock={false} closeOnOverlayClick={false} closeOnEsc={false} fullScreen={answer ? true : false} buttons={[]} isOpen={dialog.view === 'dashboardHeaderQuestion'} onClose={DialogClose}>
            <div className="mbsc-grid cs-dashboard-header-question-header-col">
                <div className="mbsc-row cs-dashboard-header-question-header-row">
                    {width > 576 &&
                        <div className="mbsc-col cs-dashboard-header-question-header-col" />
                    }
                    <div className={width > 576 ? "mbsc-col cs-dashboard-header-question-header-col mbsc-align-center" : "mbsc-col cs-dashboard-header-question-header-col"} >
                        <Button className="cs-dashboard-header-question-title-button mbsc-bold mbsc-txt-muted" variant="flat" >
                            {locale.dashboardHeaderQuestion.a}
                        </Button>
                    </div>
                    <div className="mbsc-col cs-dashboard-header-question-header-col mbsc-align-right" >
                        <Button color="primary" variant="flat" className="mbsc-bold cs-dashboard-header-question-close-button" title={locale.dashboardHeaderQuestion.f} onClick={() => DialogClose()} >
                            &nbsp;<FontAwesomeIcon className="cs-dashboard-header-question-close-icon" icon={faXmark} />
                        </Button>
                    </div>
                </div>
            </div>
            {loading ?
                <LoadingAvatar
                    avatar={quickQuestion.avatar}
                    voice={quickQuestion.voice}
                    configuration={null}
                />
                :
                <>
                    {!answer &&
                        <div className="cs-dashboard-header-question-helper">
                            <p>
                                {locale.dashboardHeaderQuestion.g}
                            </p>
                        </div>
                    }
                    <div className={answer ? "mbsc-row cs-dashboard-header-question-main cs-dashboard-header-question-scroll" : "mbsc-row cs-dashboard-header-question-main"}>
                        <div className="mbsc-col cs-dashboard-header-question-body" ref={editorWrapRef}>
                            <CKEditor
                                editor={BalloonEditor}
                                config={{
                                    toolbar: {
                                        items: !answer ? ['bold', 'italic', 'underline', 'subscript', 'superscript', 'removeFormat', '|', 'blockQuote', 'codeBlock'] : [],
                                        shouldNotGroupWhenFull: !answer ? false : true
                                    },
                                    blockToolbar: !answer ? ['bold', 'italic', '|', 'blockQuote', 'codeBlock'] : [],
                                    versionCheck: false,
                                    plugins: !answer ? [
                                        Autosave,
                                        BlockQuote,
                                        BlockToolbar,
                                        Bold,
                                        Essentials,
                                        Italic,
                                        Paragraph,
                                        RemoveFormat,
                                        Subscript,
                                        Superscript,
                                        Underline,
                                        CodeBlock
                                    ] : [
                                        Autosave,
                                        Essentials,
                                        GeneralHtmlSupport,
                                        HtmlComment,
                                        Paragraph
                                    ],
                                    htmlSupport: answer ? {
                                        allow: [
                                            {
                                                name: /^.*$/,
                                                styles: true,
                                                attributes: true,
                                                classes: true
                                            }
                                        ]
                                    } : null,
                                    licenseKey: 'GPL',
                                    ui: {
                                        poweredBy: {position:'inside', side:'right'}
                                    },
                                    placeholder: (locale.dashboardHeaderQuestion.b + " '" + quickQuestion.name + "'")
                                }}
                                data={!answer ? text : answer}
                                onChange={(event, editor) => !answer && EditorUpdate(editor.getData())}
                                disableWatchdog={true}
                                onReady={editor => {
                                    if (answer) {
                                        editor.enableReadOnlyMode('editor-dashboard-conversation');
                                    }
                                    setEditorInstance(editor);
                                }}
                            />
                        </div>
                    </div>
                    {!answer &&
                        <div className="cs-dashboard-header-question-checkbox-wrapper">
                            <Checkbox
                                color="info"
                                cssClass="cs-dashboard-header-question-checkbox mbsc-bold"
                                label={locale.dashboardHeaderQuestion.d}
                                checked={context}
                                disabled={answer}
                                onChange={(ev) => setContext(ev.target.checked)}
                            />
                        </div>
                    }
                </>
            }
            {(!answer && !loading) &&
                <div className="mbsc-button-group-justified cs-dashboard-header-question-nav-buttons">
                    <Button disabled={text.length < 8} color="primary" variant="flat" className="mbsc-bold cs-dashboard-header-question-nav-button" onClick={() => ButtonAsk()} >
                        {locale.dashboardHeaderQuestion.c}
                    </Button>
                </div>
            }
        </Popup>
    );
}

export default DashboardHeaderQuestion;
